import React, { useState, useEffect, useLayoutEffect, lazy, Suspense } from "react";
import { operatorsAuthAllowed } from "./lib/operatorsSession";
import {
  getAliSessionEmail,
  getOperatorsSessionEmail,
  setOperatorsSessionEmail,
} from "./lib/magicLinkBrowserSession";
import SEO from "./components/SEO";
import Header from "./components/Header";
import ChatApp from "./app/ChatApp";
import Contact from "./components/Contact";
import Footer from "./components/Footer";
import HomePage from "./components/home/HomePage";
import Journal from "./pages/Journal";
import JournalPost from "./pages/JournalPost";
import Podcast from "./pages/Podcast";
import PodcastEpisode from "./pages/PodcastEpisode";
import PodcastGuestIntake from "./pages/PodcastGuestIntake";
import BeforeWeTalk from "./pages/BeforeWeTalk";
import ArchyYou from "./pages/ArchyYou";
import PodcastGuestView from "./pages/PodcastGuestView";
import DevotionalPost from "./pages/DevotionalPost";
import Faith from "./pages/Faith";
import AboutPage from "./pages/About";
import ConsultingPage from "./pages/methods/Consulting";
import FractionalRolesPage from "./pages/methods/FractionalRoles";
import CCOPage from "./pages/methods/fractionalRoles/CCO";
import CTOPage from "./pages/methods/fractionalRoles/CTO";
import WhatIDoPage from "./pages/WhatIDo.jsx";
// Culture Science ALI marketing pages (remain under /culture-science/ali/*)
import ALI from "./pages/cultureScience/ALI";
import ALIApply from "./pages/cultureScience/ALIApply";
import ALIThanks from "./pages/cultureScience/ALIThanks";

// Standalone ALI SaaS pages (under /ali/*)
import ALILanding from "./pages/ali/Landing";
import ALILogin from "./pages/ali/Login";
import ALISignup from "./pages/ali/Signup";
import ALIVerifyEmail from "./pages/ali/VerifyEmail";
import ALISetupAccount from "./pages/ali/SetupAccount";
import ALIDashboardSaaS from "./pages/ali/Dashboard";
import ALIDeploy from "./pages/ali/Deploy";
import ALISettings from "./pages/ali/Settings";
import ALIBilling from "./pages/ali/Billing";
import ReportsHub from "./pages/ali/ReportsHub";
import ReportsZones from "./pages/ali/ReportsZones";
import ReportsMirror from "./pages/ali/ReportsMirror";
import ReportsProfile from "./pages/ali/ReportsProfile";
import ALISurvey from "./pages/ali/Survey";
import SuperAdminOverview from "./pages/ali/SuperAdminOverview";
import SuperAdminIntelligence from "./pages/ali/SuperAdminIntelligence";
import SuperAdminTenants from "./pages/ali/SuperAdminTenants";
import SuperAdminDeletions from "./pages/ali/SuperAdminDeletions";
import SuperAdminAuditLog from "./pages/ali/SuperAdminAuditLog";
// AO Automation Dashboard
import AOLogin from "./pages/ao/Login";
import ReviewerLogin from "./pages/ReviewerLogin";
import ReviewerDashboard from "./pages/ReviewerDashboard";
import ReviewerLibrary from "./pages/ReviewerLibrary";
import ReviewerSettings from "./pages/ReviewerSettings";
import AOCommandCenter from "./pages/ao/CommandCenter";
import AOInsights from "./pages/ao/Insights";
import AOReview from "./pages/ao/Review";
import AOReviewQueue from "./pages/ao/ReviewQueue";
import AOAutoDrafts from "./pages/ao/AutoDrafts";
import AOPublishing from "./pages/ao/Publishing";
import AOWriting from "./pages/ao/Writing";
import AOImport from "./pages/ao/Import";
import AOIdeas from "./pages/ao/Ideas";
import AOLibrary from "./pages/ao/Library";
import AOSettings from "./pages/ao/Settings";
import AOScout from "./pages/ao/Scout";
import AOLinkedInHandoff from "./pages/ao/LinkedInHandoff";
import PodcastGuestAdmin from "./pages/ao/PodcastGuestAdmin";
import PodcastGuestAdminCombined from "./pages/ao/PodcastGuestAdminCombined";
import PodcastEpisodeAdmin from "./pages/ao/PodcastEpisodeAdmin";
import PodcastDashboard from "./pages/ao/PodcastDashboard";
import AOClients from "./pages/ao/Clients";
import AOTalk from "./pages/ao/Talk";
import AORemote from "./pages/ao/Remote";
// Operators pages - Lazy loaded for better performance
const OperatorsLanding = lazy(() => import("./pages/operators/Landing"));
const OperatorsLogin = lazy(() => import("./pages/operators/Login"));
const OperatorsEvents = lazy(() => import("./pages/operators/Events"));
const OperatorsEventDetail = lazy(() => import("./pages/operators/EventDetail"));
const OperatorsCreateEvent = lazy(() => import("./pages/operators/CreateEvent"));
const OperatorsEditEvent = lazy(() => import("./pages/operators/EditEvent"));
const OperatorsDashboard = lazy(() => import("./pages/operators/Dashboard"));
const OperatorsAdmin = lazy(() => import("./pages/operators/Admin"));
const OperatorsCandidates = lazy(() => import("./pages/operators/Candidates"));
const OperatorsProfile = lazy(() => import("./pages/operators/Profile"));
import ToastProvider from "./components/operators/ToastProvider";
import { CriticalErrorProvider } from "./components/operators/CriticalErrorBanner";
import OfflineBanner from "./components/operators/OfflineBanner";
import ErrorBoundary from "./components/operators/ErrorBoundary";
import { QueryClientProvider } from "@tanstack/react-query";
import { operatorsQueryClient } from "./lib/operators/query-client";
import UserProvider from "./contexts/UserContext";
import ContactPage from "./pages/Contact";
import CultureSciencePage from "./pages/cultureScience/CultureScience";
import ScoreboardLeadership from "./pages/cultureScience/ScoreboardLeadership";
import BadLeaderProject from "./pages/cultureScience/BadLeaderProject";
import BadLeaderAdminLogin from "./pages/cultureScience/BadLeaderAdminLogin";
import BadLeaderAdmin from "./pages/cultureScience/BadLeaderAdmin";
import IndustryReports from "./pages/cultureScience/IndustryReports";
import Ethics from "./pages/cultureScience/Ethics";
import ArchyPage from "./pages/archy/Archy";
import FAQsPage from "./pages/FAQs";
import PrivacyPolicyPage from "./pages/PrivacyPolicy";
import TermsAndConditionsPage from "./pages/TermsAndConditions";
import ALIEULAPage from "./pages/ALIEULA.jsx";
import EngagementInquiryPage from "./pages/EngagementInquiry";
import AccidentalCEOPage from "./pages/AccidentalCEO";
import RemainingHumanPage from "./pages/RemainingHuman";
import OCCPage from "./pages/OCC";
import AdvisoryPage from "./pages/Advisory";
import WorkWithBartPage from "./pages/WorkWithBart";
import TheRoomPage from "./pages/TheRoom";
import BooksPage from "./pages/Books";
import GuidesPage from "./pages/Guides";
import { trackMetaPixelPageView } from "./lib/metaPixel";

export default function App() {
  // Initialize currentPage based on initial pathname
  const getInitialPage = () => {
    const path = window.location.pathname;
    if (path === '/faith') return 'faith';
    if (path === '/journal') return 'journal';
    if (path.startsWith('/journal/')) return 'journal-post';
    if (path === '/podcast') return 'podcast';
    if (/^\/podcast\/guest\/[^/]+$/.test(path)) return 'podcast-guest-view';
    if (path === '/podcast/guest-intake') return 'podcast-guest-intake';
    if (path === '/before-we-talk') return 'before-we-talk';
    // The link Bart hands someone he has offered a mentor session to. Same
    // form, same release, same pipeline: it arrives on the mentor track.
    if (path === '/podcast/mentor-session') return 'podcast-mentor-session';
    if (path.startsWith('/podcast/')) return 'podcast-episode';
    if (path === '/meet-bart' || path === '/about') return 'about';
    if (path === '/contact') return 'contact';
    if (path === '/culture-science/anti-projects/bad-leader-project/admin/login') return 'bad-leader-admin-login';
    if (path === '/culture-science/anti-projects/bad-leader-project/admin') return 'bad-leader-admin';
    if (path === '/culture-science/bad-leader-project/admin/login') return 'bad-leader-admin-login';
    if (path === '/culture-science/bad-leader-project/admin') return 'bad-leader-admin';
    if (path.startsWith('/culture-science/anti-projects/bad-leader-project/cluster/')) return 'bad-leader-project';
    if (path.startsWith('/culture-science/bad-leader-project/cluster/')) return 'bad-leader-project';
    if (path === '/faqs' || path.startsWith('/faqs')) return 'faqs';
    if (path === '/engagement-inquiry') return 'engagement-inquiry';
    if (path === '/privacy-policy' || path === '/privacy') return 'privacy-policy';
    if (path === '/terms-and-conditions' || path === '/terms' || path === '/terms-of-service') return 'terms-and-conditions';
    if (path === '/ali-eula' || path === '/eula') return 'ali-eula';
    // Standalone ALI SaaS routes (not under culture-science)
    if (path === '/ali') {
      return 'ali-landing';
    }
    if (path.startsWith('/ali/')) {
      if (path === '/ali/login') return 'ali-login';
      if (path === '/ali/signup') return 'ali-signup';
      if (path === '/ali/verify-email') return 'ali-verify-email';
      if (path === '/ali/setup-account') return 'ali-setup-account';
      if (path === '/ali/dashboard') return 'ali-dashboard';
      if (path === '/ali/deploy') return 'ali-deploy';
      if (path === '/ali/settings') return 'ali-settings';
      if (path === '/ali/billing') return 'ali-billing';
      if (path === '/ali/reports') return 'ali-reports';
      if (path === '/ali/reports/zones') return 'ali-reports-zones';
      if (path === '/ali/reports/mirror') return 'ali-reports-mirror';
      if (path === '/ali/reports/profile') return 'ali-reports-profile';
      if (path.startsWith('/ali/survey/')) return 'ali-survey';
      if (path.startsWith('/ali/super-admin/')) {
        if (path === '/ali/super-admin/overview') return 'ali-super-admin-overview';
        if (path === '/ali/super-admin/intelligence') return 'ali-super-admin-intelligence';
        if (path === '/ali/super-admin/tenants') return 'ali-super-admin-tenants';
        if (path === '/ali/super-admin/deletions') return 'ali-super-admin-deletions';
        if (path === '/ali/super-admin/audit-log') return 'ali-super-admin-audit-log';
        // Default Super Admin route to overview
        return 'ali-super-admin-overview';
      }
      // Unknown /ali/* route - redirect to landing
      return 'ali-landing';
    }
    if (path === '/culture-science' || path.startsWith('/culture-science/')) {
      return 'culture-science';
    }
    if (path === '/archy/you') return 'archy-you';
    if (path === '/archy' || path.startsWith('/archy/')) return 'archy';
    if (path === '/philosophy') return 'about';
    if (path === '/consulting') return 'methods-consulting';
    if (path === '/fractional-roles/cco') return 'methods-fractional-cco';
    if (path === '/fractional-roles/cto') return 'methods-fractional-cto';
    if (path === '/fractional-roles') return 'methods-fractional-roles';
    if (path === '/what-i-do') return 'what-i-do';
    if (path === '/accidental-ceo') return 'accidental-ceo';
    if (path === '/the-room') return 'the-room';
    if (path === '/books') return 'books';
    if (path === '/guides' || path.startsWith('/guides/')) return 'guides';
    if (path === '/advisory') return 'advisory';
    if (path === '/work-with-bart') return 'work-with-bart';
    if (path === '/remaining-human') return 'remaining-human';
    if (path === '/occ' || path === '/occ/') return 'occ';
    // Operators Platform routes
    if (path === '/operators') return 'operators-landing';
    if (path === '/operators/login') return 'operators-login';
    // Check authentication for protected Operators routes
    if (path.startsWith('/operators/')) {
      // Public routes (already handled above)
      if (path === '/operators' || path === '/operators/login') {
        // This shouldn't happen due to checks above, but just in case
        return path === '/operators' ? 'operators-landing' : 'operators-login';
      }
      // Protected routes - check authentication
      if (typeof window !== 'undefined' && !operatorsAuthAllowed()) {
        // Not authenticated - will redirect to login in handleRoute
        // But for now, return login page to prevent flash of protected content
        return 'operators-login';
      }
      // Authenticated - return appropriate page based on path
      if (path === '/operators/events') return 'operators-events';
      if (path === '/operators/events/new') return 'operators-create-event';
      if (path.startsWith('/operators/events/')) {
        const eventPath = path.replace('/operators/events/', '');
        if (eventPath === 'new') return 'operators-create-event';
        if (eventPath.endsWith('/edit')) return 'operators-edit-event';
        if (eventPath) return 'operators-event-detail';
        return 'operators-events';
      }
      if (path === '/operators/dashboard') return 'operators-dashboard';
      if (path === '/operators/admin') return 'operators-admin';
      if (path === '/operators/candidates') return 'operators-candidates';
      if (path === '/operators/profile') return 'operators-profile';
      // Unknown operators route - redirect to landing
      return 'operators-landing';
    }
    // AO Automation Dashboard routes
    if (path === '/ao' || path === '/ao/') {
      return 'ao-command-center';
    }
    if (path === '/ao/login') return 'ao-login';
    if (path === '/ao/reviewer-login') return 'ao-reviewer-login';
    if (path === '/ao/reviewer/library') return 'ao-reviewer-library';
    if (path === '/ao/reviewer/settings') return 'ao-reviewer-settings';
    if (path === '/ao/reviewer') return 'ao-reviewer';
    if (path === '/ao/auth/linkedin/handoff') return 'ao-linkedin-handoff';
    if (path.startsWith('/ao/')) {
      if (path === '/ao/command-center') return 'ao-command-center';
      if (path === '/ao/import') return 'ao-import';
      if (path === '/ao/library/review-queue') return 'ao-review-queue';
      if (path === '/ao/library/drafts') return 'ao-auto-drafts';
      if (path === '/ao/library') return 'ao-library';
      if (path === '/ao/ideas') return 'ao-ideas';
      if (path === '/ao/scout') return 'ao-scout';
      if (path === '/ao/analyst') return 'ao-review';
      if (path === '/ao/studio') return 'ao-writing';
      if (path === '/ao/publisher') return 'ao-publishing';
      if (path === '/ao/insights') return 'ao-insights';
      if (path === '/ao/review') return 'ao-review';
      if (path === '/ao/publishing') return 'ao-publishing';
      if (path === '/ao/writing') return 'ao-writing';
      if (path === '/ao/settings') return 'ao-settings';
      if (path === '/ao/podcast') return 'ao-podcast';
      if (path === '/ao/clients' || path === '/ao/crm') return 'ao-clients';
      if (path === '/ao/talk') return 'ao-talk';
      if (path === '/ao/remote') return 'ao-remote';
      if (/^\/ao\/podcast\/episode\/[^/]+$/.test(path)) return 'ao-podcast-episode';
      if (/^\/ao\/podcast\/guest-combined\/[^/]+$/.test(path)) return 'ao-podcast-guest-combined';
      if (/^\/ao\/podcast\/guest\/[^/]+$/.test(path)) return 'ao-podcast-guest';
      return 'ao-command-center';
    }
    return 'home';
  };
  
  const [currentPage, setCurrentPage] = useState(getInitialPage());

  // Immediate route protection check on mount for protected Operators routes
  useEffect(() => {
    const path = window.location.pathname;
    if (path.startsWith('/operators/') && path !== '/operators' && path !== '/operators/login') {
      if (typeof window !== 'undefined' && !operatorsAuthAllowed()) {
        window.location.replace('/operators/login');
        return;
      }
    }
  }, []);

  // AO Automation routes are protected server-side via signed session cookie.

  useEffect(() => {
    // Handle routing and redirects
    const handleRoute = () => {
      let path = window.location.pathname;

      // AO Automation legacy routes -> bot-stage pipeline routes
      if (path === '/ao/review') {
        window.history.replaceState({}, '', '/ao/analyst');
        path = '/ao/analyst';
      } else if (path === '/ao/publishing') {
        window.history.replaceState({}, '', '/ao/publisher');
        path = '/ao/publisher';
      } else if (path === '/ao/writing') {
        window.history.replaceState({}, '', '/ao/studio');
        path = '/ao/studio';
      } else if (path === '/ao/insights') {
        window.history.replaceState({}, '', '/ao/scout');
        path = '/ao/scout';
      }
      
      // Standalone ALI SaaS routes
      if (path === '/ali') {
        setCurrentPage('ali-landing');
        return;
      }
      if (path.startsWith('/ali/')) {
        const hasAliEmail = () => {
          if (typeof window === 'undefined') return false;
          const e = (new URLSearchParams(window.location.search).get('email') || getAliSessionEmail() || '').trim();
          return e.length > 0;
        };

        if (path === '/ali/login') {
          setCurrentPage('ali-login');
        } else if (path === '/ali/signup') {
          setCurrentPage('ali-signup');
        } else if (path === '/ali/verify-email') {
          setCurrentPage('ali-verify-email');
        } else if (path === '/ali/setup-account') {
          setCurrentPage('ali-setup-account');
        } else if (path.startsWith('/ali/survey/')) {
          setCurrentPage('ali-survey');
        } else if (path === '/ali/dashboard') {
          if (!hasAliEmail()) { window.location.replace('/ali/login'); return; }
          setCurrentPage('ali-dashboard');
        } else if (path === '/ali/deploy') {
          if (!hasAliEmail()) { window.location.replace('/ali/login'); return; }
          setCurrentPage('ali-deploy');
        } else if (path === '/ali/settings') {
          if (!hasAliEmail()) { window.location.replace('/ali/login'); return; }
          setCurrentPage('ali-settings');
        } else if (path === '/ali/billing') {
          if (!hasAliEmail()) { window.location.replace('/ali/login'); return; }
          setCurrentPage('ali-billing');
        } else if (path === '/ali/reports') {
          if (!hasAliEmail()) { window.location.replace('/ali/login'); return; }
          setCurrentPage('ali-reports');
        } else if (path === '/ali/reports/zones') {
          if (!hasAliEmail()) { window.location.replace('/ali/login'); return; }
          setCurrentPage('ali-reports-zones');
        } else if (path === '/ali/reports/mirror') {
          if (!hasAliEmail()) { window.location.replace('/ali/login'); return; }
          setCurrentPage('ali-reports-mirror');
        } else if (path === '/ali/reports/profile') {
          if (!hasAliEmail()) { window.location.replace('/ali/login'); return; }
          setCurrentPage('ali-reports-profile');
        } else if (path.startsWith('/ali/super-admin/')) {
          if (!hasAliEmail()) { window.location.replace('/ali/login'); return; }
          if (path === '/ali/super-admin/overview') {
            setCurrentPage('ali-super-admin-overview');
          } else if (path === '/ali/super-admin/intelligence') {
            setCurrentPage('ali-super-admin-intelligence');
          } else if (path === '/ali/super-admin/tenants') {
            setCurrentPage('ali-super-admin-tenants');
          } else if (path === '/ali/super-admin/deletions') {
            setCurrentPage('ali-super-admin-deletions');
          } else if (path === '/ali/super-admin/audit-log') {
            setCurrentPage('ali-super-admin-audit-log');
          } else {
            // Default Super Admin route to overview
            window.history.replaceState({}, '', '/ali/super-admin/overview');
            setCurrentPage('ali-super-admin-overview');
          }
        } else {
          // Unknown /ali/* route - redirect to landing
          window.history.replaceState({}, '', '/ali');
          setCurrentPage('ali-landing');
        }
        return;
      }
      
      // Handle Operators routes
      if (path === '/operators') {
        setCurrentPage('operators-landing');
        return;
      }
      if (path === '/operators/login') {
        setCurrentPage('operators-login');
        return;
      }
      if (path.startsWith('/operators/')) {
        // Route protection: check if user is authenticated
        const storedEmail = typeof window !== 'undefined' ? getOperatorsSessionEmail() : null;
        const urlEmail = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('email') : null;
        
        // If email comes from URL (magic link verification), store it in localStorage
        if (urlEmail && !storedEmail) {
          try {
            setOperatorsSessionEmail(urlEmail);
            // Clear email from URL for clean URLs
            const cleanUrl = window.location.pathname;
            window.history.replaceState({}, '', cleanUrl);
          } catch (e) {
            console.error('Failed to store email in localStorage:', e);
          }
        }
        
        // Check authentication (allow if email in localStorage, URL, or short-lived env bypass)
        // Only check for protected routes (not landing or login)
        if (path !== '/operators' && path !== '/operators/login') {
          if (!operatorsAuthAllowed()) {
            // Not authenticated - redirect to login
            window.location.replace('/operators/login');
            return;
          }
        }
        
        // User is authenticated (or on public route), proceed with route handling
        if (path === '/operators/events') {
          setCurrentPage('operators-events');
        } else if (path === '/operators/events/new') {
          setCurrentPage('operators-create-event');
        } else if (path.startsWith('/operators/events/')) {
          const eventPath = path.replace('/operators/events/', '');
          if (eventPath === 'new') {
            setCurrentPage('operators-create-event');
          } else if (eventPath.endsWith('/edit')) {
            setCurrentPage('operators-edit-event');
          } else if (eventPath) {
            setCurrentPage('operators-event-detail');
          } else {
            setCurrentPage('operators-events');
          }
        } else if (path === '/operators/dashboard') {
          setCurrentPage('operators-dashboard');
        } else if (path === '/operators/admin') {
          setCurrentPage('operators-admin');
        } else if (path === '/operators/candidates') {
          setCurrentPage('operators-candidates');
        } else if (path === '/operators/profile') {
          setCurrentPage('operators-profile');
        } else {
          // Unknown operators route - redirect to landing (not dashboard)
          window.history.replaceState({}, '', '/operators');
          setCurrentPage('operators-landing');
        }
        return;
      }

      // AO Automation Dashboard routes
      if (path === '/ao' || path === '/ao/') {
        window.history.replaceState({}, '', '/ao/analyst');
        setCurrentPage('ao-review');
        return;
      }
      if (path === '/ao/login') {
        setCurrentPage('ao-login');
        return;
      }
      if (path === '/ao/reviewer-login') {
        setCurrentPage('ao-reviewer-login');
        return;
      }
      if (path === '/ao/reviewer/library') {
        setCurrentPage('ao-reviewer-library');
        return;
      }
      if (path === '/ao/reviewer/settings') {
        setCurrentPage('ao-reviewer-settings');
        return;
      }
      if (path === '/ao/reviewer') {
        setCurrentPage('ao-reviewer');
        return;
      }
      if (path.startsWith('/ao/')) {
        if (path === '/ao/auth/linkedin/handoff') {
          setCurrentPage('ao-linkedin-handoff');
          return;
        }
        if (path === '/ao/command-center') {
          window.history.replaceState({}, '', '/ao/analyst');
          setCurrentPage('ao-review');
        }
        else if (path === '/ao/scout') setCurrentPage('ao-scout');
        else if (path === '/ao/analyst') setCurrentPage('ao-review');
        else if (path === '/ao/studio') setCurrentPage('ao-writing');
        else if (path === '/ao/publisher') setCurrentPage('ao-publishing');
        else if (path === '/ao/import') setCurrentPage('ao-import');
        else if (path === '/ao/library/review-queue') setCurrentPage('ao-review-queue');
        else if (path === '/ao/library/drafts') setCurrentPage('ao-auto-drafts');
        else if (path === '/ao/library') setCurrentPage('ao-library');
        else if (path === '/ao/ideas') {
          window.history.replaceState({}, '', '/ao/library');
          setCurrentPage('ao-ideas');
        }
        else if (path === '/ao/insights') setCurrentPage('ao-insights');
        else if (path === '/ao/review') setCurrentPage('ao-review');
        else if (path === '/ao/publishing') setCurrentPage('ao-publishing');
        else if (path === '/ao/writing') setCurrentPage('ao-writing');
        else if (path === '/ao/settings') setCurrentPage('ao-settings');
        else if (path === '/ao/podcast') setCurrentPage('ao-podcast');
        else if (path === '/ao/clients' || path === '/ao/crm') setCurrentPage('ao-clients');
        else if (path === '/ao/talk') setCurrentPage('ao-talk');
        else if (path === '/ao/remote') setCurrentPage('ao-remote');
        else if (/^\/ao\/podcast\/episode\/[^/]+$/.test(path)) setCurrentPage('ao-podcast-episode');
        else if (/^\/ao\/podcast\/guest-combined\/[^/]+$/.test(path)) setCurrentPage('ao-podcast-guest-combined');
        else if (/^\/ao\/podcast\/guest\/[^/]+$/.test(path)) setCurrentPage('ao-podcast-guest');
        else {
          window.history.replaceState({}, '', '/ao/analyst');
          setCurrentPage('ao-review');
        }
        return;
      }
      
      // Redirect old culture-science/ali routes to standalone /ali routes (preserve existing marketing pages)
      if (path === '/culture-science/ali' || path.startsWith('/culture-science/ali/')) {
        // Keep marketing pages under culture-science, only redirect SaaS routes
        if (path === '/culture-science/ali/apply') {
          window.history.replaceState({}, '', '/ali/signup');
          setCurrentPage('ali-signup');
        } else if (path === '/culture-science/ali/thanks') {
          // Keep thanks page or redirect appropriately
          setCurrentPage('ali-thanks');
        } else {
          // All other culture-science/ali/* remain as marketing pages
          if (path === '/culture-science/ali/dashboard' || path === '/culture-science/ali/dashboard/') {
            window.history.replaceState({}, '', '/ali/dashboard');
            setCurrentPage('ali-dashboard');
          } else if (
            path === '/culture-science/ali/why-ali-exists' ||
            path === '/culture-science/ali/method' ||
            path === '/culture-science/ali/early-warning' ||
            path === '/culture-science/ali/early-warning-indicators' ||
            path === '/culture-science/ali/seven-leadership-conditions' ||
            path === '/culture-science/ali/seven-leadership-conditions/' ||
            path === '/culture-science/ali/six-leadership-conditions' ||
            path === '/culture-science/ali/six-leadership-conditions/' ||
            path === '/culture-science/ali/faqs'
          ) {
            window.history.replaceState({}, '', '/culture-science/ali');
            setCurrentPage('ali');
          } else {
            setCurrentPage('ali');
          }
        }
        return;
      }
      
      // Route detection
      if (path === '/' || path === '') {
        setCurrentPage('home');
      } else if (path === '/faith') {
        setCurrentPage('faith');
      } else if (path === '/journal') {
        setCurrentPage('journal');
      } else if (path.startsWith('/journal/')) {
        // Check if it's a devotional or journal post
        // We'll determine this in the component based on the post type
        setCurrentPage('journal-post');
      } else if (path === '/podcast') {
        setCurrentPage('podcast');
      } else if (/^\/podcast\/guest\/[^/]+$/.test(path)) {
        setCurrentPage('podcast-guest-view');
      } else if (path === '/podcast/guest-intake') {
        setCurrentPage('podcast-guest-intake');
      } else if (path === '/before-we-talk') {
        setCurrentPage('before-we-talk');
      } else if (path === '/podcast/mentor-session') {
        setCurrentPage('podcast-mentor-session');
      } else if (path.startsWith('/podcast/')) {
        setCurrentPage('podcast-episode');
      } else if (path === '/meet-bart') {
        setCurrentPage('about');
      } else if (path === '/philosophy') {
        window.history.replaceState({}, '', '/meet-bart');
        setCurrentPage('about');
        return;
      } else if (path === '/about') {
        // Redirect old /about to /meet-bart
        window.history.replaceState({}, '', '/meet-bart');
        setCurrentPage('about');
        return;
      } else if (path === '/contact') {
        setCurrentPage('contact');
      } else if (path === '/faqs' || path.startsWith('/faqs')) {
        setCurrentPage('faqs');
      } else if (path === '/engagement-inquiry') {
        setCurrentPage('engagement-inquiry');
      } else if (path === '/privacy-policy' || path === '/privacy') {
        setCurrentPage('privacy-policy');
      } else if (path === '/terms-and-conditions' || path === '/terms' || path === '/terms-of-service') {
        setCurrentPage('terms-and-conditions');
      } else if (path === '/culture-science' || path.startsWith('/culture-science/')) {
        if (path === '/culture-science/ali' || path.startsWith('/culture-science/ali/')) {
          if (path === '/culture-science/ali/apply') {
            setCurrentPage('ali-apply');
          } else if (path === '/culture-science/ali/thanks') {
            setCurrentPage('ali-thanks');
          } else if (path === '/culture-science/ali/dashboard' || path === '/culture-science/ali/dashboard/') {
            window.history.replaceState({}, '', '/ali/dashboard');
            setCurrentPage('ali-dashboard');
          } else if (
            path === '/culture-science/ali/why-ali-exists' ||
            path === '/culture-science/ali/method' ||
            path === '/culture-science/ali/early-warning' ||
            path === '/culture-science/ali/early-warning-indicators' ||
            path === '/culture-science/ali/seven-leadership-conditions' ||
            path === '/culture-science/ali/seven-leadership-conditions/' ||
            path === '/culture-science/ali/six-leadership-conditions' ||
            path === '/culture-science/ali/six-leadership-conditions/' ||
            path === '/culture-science/ali/faqs'
          ) {
            window.history.replaceState({}, '', '/culture-science/ali');
            setCurrentPage('ali');
          } else {
            setCurrentPage('ali');
          }
        } else if (path === '/culture-science/anti-projects/scoreboard-leadership') {
          setCurrentPage('scoreboard-leadership');
        } else if (path === '/culture-science/scoreboard-leadership') {
          setCurrentPage('scoreboard-leadership');
        } else if (path === '/culture-science/anti-projects/bad-leader-project') {
          setCurrentPage('bad-leader-project');
        } else if (path.startsWith('/culture-science/anti-projects/bad-leader-project/cluster/')) {
          setCurrentPage('bad-leader-project');
        } else if (path === '/culture-science/anti-projects/bad-leader-project/admin/login') {
          setCurrentPage('bad-leader-admin-login');
        } else if (path === '/culture-science/anti-projects/bad-leader-project/admin') {
          setCurrentPage('bad-leader-admin');
        } else if (path === '/culture-science/bad-leader-project/admin/login') {
          setCurrentPage('bad-leader-admin-login');
        } else if (path === '/culture-science/bad-leader-project/admin') {
          setCurrentPage('bad-leader-admin');
        } else if (path === '/culture-science/bad-leader-project') {
          setCurrentPage('bad-leader-project');
        } else if (path.startsWith('/culture-science/bad-leader-project/cluster/')) {
          setCurrentPage('bad-leader-project');
        } else if (path === '/culture-science/research') {
          window.history.replaceState({}, '', '/culture-science');
          setCurrentPage('culture-science');
        } else if (path === '/culture-science/industry-reports') {
          window.history.replaceState({}, '', '/culture-science');
          setCurrentPage('culture-science');
        } else if (path === '/culture-science/ethics') {
          window.history.replaceState({}, '', '/culture-science/ali');
          setCurrentPage('ali');
        } else {
          setCurrentPage('culture-science');
        }
      } else if (path === '/archy/you') {
        setCurrentPage('archy-you');
      } else if (path === '/archy' || path.startsWith('/archy/')) {
        // Only allow main Archy page - placeholder subpages removed
        setCurrentPage('archy');
      } else if (path === '/consulting') {
        setCurrentPage('methods-consulting');
      } else if (path === '/fractional-roles/cco') {
        setCurrentPage('methods-fractional-cco');
      } else if (path === '/fractional-roles/cto') {
        setCurrentPage('methods-fractional-cto');
      } else if (path === '/fractional-roles') {
        setCurrentPage('methods-fractional-roles');
      } else if (path === '/what-i-do') {
        setCurrentPage('what-i-do');
      } else if (path === '/accidental-ceo') {
        setCurrentPage('accidental-ceo');
      } else if (path === '/the-room') {
        setCurrentPage('the-room');
      } else if (path === '/books') {
        setCurrentPage('books');
      } else if (path === '/guides' || path.startsWith('/guides/')) {
        setCurrentPage('guides');
      } else if (path === '/advisory') {
        setCurrentPage('advisory');
      } else if (path === '/work-with-bart') {
        setCurrentPage('work-with-bart');
      } else if (path === '/remaining-human') {
        setCurrentPage('remaining-human');
      } else if (path === '/occ' || path === '/occ/') {
        setCurrentPage('occ');
      } else {
        setCurrentPage('home');
      }
    };

    const handlePopState = () => {
      handleRoute();
      trackMetaPixelPageView();
    };

    window.addEventListener('popstate', handlePopState);
    handleRoute(); // Check initial route (Meta Pixel PageView already fired from index.html)

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  /**
   * Scroll position after in-app navigation was broken: we treated "destination URL has a saved
   * scroll in sessionStorage" as "user pressed Back," so visiting a page you had scrolled on
   * before jumped you to the old scroll position (often the footer). Marketing routes now always
   * reset to top on route change; optional #hash targets scroll after paint.
   */
  useLayoutEffect(() => {
    const path = window.location.pathname;
    const hash = window.location.hash;
    const isJournalPage = path === '/journal' || path.startsWith('/journal/');
    const isPodcastPage = path === '/podcast' || path.startsWith('/podcast/');

    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }

    if (isJournalPage || isPodcastPage) {
      const scrollToTop = () => window.scrollTo(0, 0);
      scrollToTop();
      requestAnimationFrame(() => {
        scrollToTop();
        requestAnimationFrame(scrollToTop);
      });
      const timers = [0, 10, 50, 100, 200, 300, 500].map((delay) =>
        setTimeout(scrollToTop, delay)
      );
      return () => timers.forEach(clearTimeout);
    }

    const scrollToHashOrTop = () => {
      if (hash && hash.length > 1) {
        const id = decodeURIComponent(hash.slice(1));
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: 'instant', block: 'start' });
          return;
        }
      }
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    };

    scrollToHashOrTop();
    requestAnimationFrame(() => {
      scrollToHashOrTop();
    });

    let t = setTimeout(scrollToHashOrTop, 100);
    return () => clearTimeout(t);
  }, [currentPage]);

  // Render About page
  if (currentPage === 'about') {
    return (
      <main className="bg-warm-offWhite text-warm-charcoal">
        <Header />
        <AboutPage />
        <Footer />
      </main>
    );
  }

  // Render Methods Consulting page
  if (currentPage === 'methods-consulting') {
    return (
      <main className="bg-warm-offWhite text-warm-charcoal">
        <Header />
        <ConsultingPage />
        <Footer />
      </main>
    );
  }

  // Render Methods Fractional Roles page
  if (currentPage === 'methods-fractional-roles') {
    return (
      <main className="bg-warm-offWhite text-warm-charcoal">
        <Header />
        <FractionalRolesPage />
        <Footer />
      </main>
    );
  }

  // Render Methods Fractional CCO page
  if (currentPage === 'methods-fractional-cco') {
    return (
      <main className="bg-warm-offWhite text-warm-charcoal">
        <Header />
        <CCOPage />
        <Footer />
      </main>
    );
  }

  // Render Methods Fractional CTO page
  if (currentPage === 'methods-fractional-cto') {
    return (
      <main className="bg-warm-offWhite text-warm-charcoal">
        <Header />
        <CTOPage />
        <Footer />
      </main>
    );
  }

  // Render What I Do page
  if (currentPage === 'what-i-do') {
    return (
      <main className="bg-warm-offWhite text-warm-charcoal">
        <Header />
        <WhatIDoPage />
        <Footer />
      </main>
    );
  }

  // Accidental CEO book landing
  if (currentPage === 'accidental-ceo') {
    return (
      <main className="bg-warm-offWhite text-warm-charcoal">
        <Header />
        <AccidentalCEOPage />
        <Footer />
      </main>
    );
  }

  if (currentPage === 'the-room') {
    return (
      <main className="bg-stone-50 text-stone-900">
        <Header />
        <TheRoomPage />
        <Footer />
      </main>
    );
  }

  if (currentPage === 'guides') {
    return (
      <main className="bg-warm-offWhite text-warm-charcoal">
        <Header />
        <GuidesPage key={window.location.pathname} />
        <Footer />
      </main>
    );
  }

  if (currentPage === 'books') {
    return (
      <main className="bg-warm-offWhite text-warm-charcoal">
        <Header />
        <BooksPage />
        <Footer />
      </main>
    );
  }

  if (currentPage === 'work-with-bart') {
    return (
      <main className="bg-stone-50 text-stone-900">
        <Header />
        <WorkWithBartPage />
        <Footer />
      </main>
    );
  }

  if (currentPage === 'advisory') {
    return (
      <main className="bg-stone-50 text-stone-900">
        <Header />
        <AdvisoryPage />
        <Footer />
      </main>
    );
  }

  // One talk's landing page, reached by QR code only (no global nav/footer).
  if (currentPage === 'occ') {
    return <OCCPage />;
  }

  // Remaining Human standalone landing page (no global nav/footer)
  if (currentPage === 'remaining-human') {
    return (
      <>
        <RemainingHumanPage />
      </>
    );
  }

  // Render Faith page
  if (currentPage === 'faith') {
    return (
      <main className="bg-warm-offWhite text-warm-charcoal">
        <Faith />
        <Footer />
      </main>
    );
  }

  // A client's private Archy, by signed link
  if (currentPage === 'archy-you') {
    return (
      <main className="bg-warm-offWhite text-warm-charcoal">
        <Header />
        <ArchyYou />
        <Footer />
      </main>
    );
  }

  // Before we talk: the private prep form sent after each booking
  if (currentPage === 'before-we-talk') {
    return (
      <main className="bg-warm-offWhite text-warm-charcoal">
        <Header />
        <BeforeWeTalk />
        <Footer />
      </main>
    );
  }

  // Render Podcast guest intake page
  if (currentPage === 'podcast-guest-intake' || currentPage === 'podcast-mentor-session') {
    return (
      <main className="bg-warm-offWhite text-warm-charcoal">
        <Header />
        <PodcastGuestIntake track={currentPage === 'podcast-mentor-session' ? 'mentor' : null} />
        <Footer />
      </main>
    );
  }

  // Render Podcast guest private show page
  if (currentPage === 'podcast-guest-view') {
    return (
      <main className="bg-warm-offWhite text-warm-charcoal">
        <PodcastGuestView />
      </main>
    );
  }

  // Render Podcast episode page
  if (currentPage === 'podcast-episode') {
    return (
      <main className="bg-warm-offWhite text-warm-charcoal">
        <Header />
        <PodcastEpisode />
        <Footer />
      </main>
    );
  }

  // Render Podcast listing page
  if (currentPage === 'podcast') {
    return (
      <main className="bg-warm-offWhite text-warm-charcoal">
        <Header />
        <Podcast />
        <Footer />
      </main>
    );
  }

  // Render Journal post page (handles both journal posts and devotionals)
  if (currentPage === 'journal-post') {
    return (
      <main className="bg-warm-offWhite text-warm-charcoal">
        <Header />
        <JournalPost />
        <Footer />
      </main>
    );
  }

  // Render Journal page
  if (currentPage === 'journal') {
    return (
      <main className="bg-warm-offWhite text-warm-charcoal">
        <Header />
        <Journal />
        <Footer />
      </main>
    );
  }

  // Render ALI landing page
  if (currentPage === 'ali') {
    return (
      <main className="bg-warm-offWhite text-warm-charcoal">
        <Header />
        <ALI />
        <Footer />
      </main>
    );
  }

  // Render ALI apply page
  if (currentPage === 'ali-apply') {
    return (
      <main className="bg-warm-offWhite text-warm-charcoal">
        <Header />
        <ALIApply />
        <Footer />
      </main>
    );
  }

  // Render ALI thanks page
  if (currentPage === 'ali-thanks') {
    return (
      <main className="bg-warm-offWhite text-warm-charcoal">
        <Header />
        <ALIThanks />
        <Footer />
      </main>
    );
  }

  // Render standalone ALI SaaS pages (no Header/Footer/Archy)
  if (currentPage === 'ali-landing') {
    return <ALILanding />;
  }

  if (currentPage === 'ali-login') {
    return <ALILogin />;
  }

  if (currentPage === 'ali-signup') {
    return <ALISignup />;
  }

  if (currentPage === 'ali-verify-email') {
    return <ALIVerifyEmail />;
  }

  if (currentPage === 'ali-setup-account') {
    return <ALISetupAccount />;
  }

  if (currentPage === 'ali-dashboard') {
    return <ALIDashboardSaaS />;
  }

  if (currentPage === 'ali-deploy') {
    return <ALIDeploy />;
  }

  if (currentPage === 'ali-settings') {
    return <ALISettings />;
  }

  if (currentPage === 'ali-billing') {
    return <ALIBilling />;
  }

  if (currentPage === 'ali-reports') {
    return <ReportsHub />;
  }

  if (currentPage === 'ali-reports-zones') {
    return <ReportsZones />;
  }

  if (currentPage === 'ali-reports-mirror') {
    return <ReportsMirror />;
  }

  if (currentPage === 'ali-reports-profile') {
    return <ReportsProfile />;
  }

  if (currentPage === 'ali-survey') {
    return <ALISurvey />;
  }

  if (currentPage === 'ali-super-admin-overview') {
    return <SuperAdminOverview />;
  }

  if (currentPage === 'ali-super-admin-intelligence') {
    return <SuperAdminIntelligence />;
  }

  if (currentPage === 'ali-super-admin-tenants') {
    return <SuperAdminTenants />;
  }

  if (currentPage === 'ali-super-admin-deletions') {
    return <SuperAdminDeletions />;
  }

  if (currentPage === 'ali-super-admin-audit-log') {
    return <SuperAdminAuditLog />;
  }

  // Loading fallback for lazy-loaded Operators pages
  const OperatorsPageLoader = () => (
    <div className="min-h-screen bg-[#fafafa] flex items-center justify-center">
      <div className="text-center">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-4"></div>
        <p className="text-gray-600">Loading...</p>
      </div>
    </div>
  );

  // Render Operators landing page (public, no auth required)
  if (currentPage === 'operators-landing') {
    return (
      <Suspense fallback={<OperatorsPageLoader />}>
        <OperatorsLanding />
      </Suspense>
    );
  }

  // Render Operators login page (public, no auth required)
  if (currentPage === 'operators-login') {
    return (
      <Suspense fallback={<OperatorsPageLoader />}>
        <OperatorsLogin />
      </Suspense>
    );
  }

  if (currentPage === 'ao-login') {
    return <AOLogin />;
  }
  if (currentPage === 'ao-reviewer-login') {
    return <ReviewerLogin />;
  }
  if (currentPage === 'ao-reviewer-library') {
    return <ReviewerLibrary />;
  }
  if (currentPage === 'ao-reviewer-settings') {
    return <ReviewerSettings />;
  }
  if (currentPage === 'ao-reviewer') {
    return <ReviewerDashboard />;
  }
  if (currentPage === 'ao-command-center') {
    return <AOCommandCenter />;
  }
  if (currentPage === 'ao-scout') {
    return <AOScout />;
  }
  if (currentPage === 'ao-import') {
    return <AOImport />;
  }
  if (currentPage === 'ao-library') {
    return <AOLibrary />;
  }
  if (currentPage === 'ao-ideas') {
    return <AOIdeas />;
  }
  if (currentPage === 'ao-auto-drafts') {
    return <AOAutoDrafts />;
  }
  if (currentPage === 'ao-review-queue') {
    return <AOReviewQueue />;
  }
  if (currentPage === 'ao-insights') {
    return <AOInsights />;
  }
  if (currentPage === 'ao-review') {
    return <AOReview />;
  }
  if (currentPage === 'ao-publishing') {
    return <AOPublishing />;
  }
  if (currentPage === 'ao-writing') {
    return <AOWriting />;
  }
  if (currentPage === 'ao-settings') {
    return <AOSettings />;
  }
  if (currentPage === 'ao-linkedin-handoff') {
    return <AOLinkedInHandoff />;
  }
  if (currentPage === 'ao-podcast-guest') {
    return <PodcastGuestAdmin />;
  }
  if (currentPage === 'ao-podcast-episode') {
    return <PodcastEpisodeAdmin />;
  }
  if (currentPage === 'ao-podcast-guest-combined') {
    return <PodcastGuestAdminCombined />;
  }
  if (currentPage === 'ao-podcast') {
    return <PodcastDashboard />;
  }
  if (currentPage === 'ao-clients') {
    return <AOClients />;
  }
  if (currentPage === 'ao-talk') {
    return <AOTalk />;
  }
  if (currentPage === 'ao-remote') {
    return <AORemote />;
  }

  // Render Operators pages with lazy loading (protected routes)
  if (currentPage === 'operators-events') {
    return (
      <ErrorBoundary>
        <UserProvider>
          <CriticalErrorProvider>
            <OfflineBanner />
            <QueryClientProvider client={operatorsQueryClient}>
              <ToastProvider>
                <Suspense fallback={<OperatorsPageLoader />}>
                  <OperatorsEvents />
                </Suspense>
              </ToastProvider>
            </QueryClientProvider>
          </CriticalErrorProvider>
        </UserProvider>
      </ErrorBoundary>
    );
  }

  if (currentPage === 'operators-create-event') {
    return (
      <ErrorBoundary>
        <UserProvider>
          <CriticalErrorProvider>
            <OfflineBanner />
            <QueryClientProvider client={operatorsQueryClient}>
              <ToastProvider>
                <Suspense fallback={<OperatorsPageLoader />}>
                  <OperatorsCreateEvent />
                </Suspense>
              </ToastProvider>
            </QueryClientProvider>
          </CriticalErrorProvider>
        </UserProvider>
      </ErrorBoundary>
    );
  }

  if (currentPage === 'operators-edit-event') {
    return (
      <ErrorBoundary>
        <UserProvider>
          <CriticalErrorProvider>
            <OfflineBanner />
            <QueryClientProvider client={operatorsQueryClient}>
              <ToastProvider>
                <Suspense fallback={<OperatorsPageLoader />}>
                  <OperatorsEditEvent />
                </Suspense>
              </ToastProvider>
            </QueryClientProvider>
          </CriticalErrorProvider>
        </UserProvider>
      </ErrorBoundary>
    );
  }

  if (currentPage === 'operators-event-detail') {
    return (
      <ErrorBoundary>
        <UserProvider>
          <CriticalErrorProvider>
            <OfflineBanner />
            <QueryClientProvider client={operatorsQueryClient}>
              <ToastProvider>
                <Suspense fallback={<OperatorsPageLoader />}>
                  <OperatorsEventDetail />
                </Suspense>
              </ToastProvider>
            </QueryClientProvider>
          </CriticalErrorProvider>
        </UserProvider>
      </ErrorBoundary>
    );
  }

  if (currentPage === 'operators-dashboard') {
    return (
      <ErrorBoundary>
        <UserProvider>
          <CriticalErrorProvider>
            <OfflineBanner />
            <QueryClientProvider client={operatorsQueryClient}>
              <ToastProvider>
                <Suspense fallback={<OperatorsPageLoader />}>
                  <OperatorsDashboard />
                </Suspense>
              </ToastProvider>
            </QueryClientProvider>
          </CriticalErrorProvider>
        </UserProvider>
      </ErrorBoundary>
    );
  }

  if (currentPage === 'operators-admin') {
    return (
      <ErrorBoundary>
        <UserProvider>
          <CriticalErrorProvider>
            <OfflineBanner />
            <QueryClientProvider client={operatorsQueryClient}>
              <ToastProvider>
                <Suspense fallback={<OperatorsPageLoader />}>
                  <OperatorsAdmin />
                </Suspense>
              </ToastProvider>
            </QueryClientProvider>
          </CriticalErrorProvider>
        </UserProvider>
      </ErrorBoundary>
    );
  }

  if (currentPage === 'operators-candidates') {
    return (
      <ErrorBoundary>
        <UserProvider>
          <CriticalErrorProvider>
            <OfflineBanner />
            <QueryClientProvider client={operatorsQueryClient}>
              <ToastProvider>
                <Suspense fallback={<OperatorsPageLoader />}>
                  <OperatorsCandidates />
                </Suspense>
              </ToastProvider>
            </QueryClientProvider>
          </CriticalErrorProvider>
        </UserProvider>
      </ErrorBoundary>
    );
  }

  if (currentPage === 'operators-profile') {
    return (
      <ErrorBoundary>
        <UserProvider>
          <CriticalErrorProvider>
            <OfflineBanner />
            <QueryClientProvider client={operatorsQueryClient}>
              <ToastProvider>
                <Suspense fallback={<OperatorsPageLoader />}>
                  <OperatorsProfile />
                </Suspense>
              </ToastProvider>
            </QueryClientProvider>
          </CriticalErrorProvider>
        </UserProvider>
      </ErrorBoundary>
    );
  }

  // Render FAQs page
  if (currentPage === 'faqs') {
    return (
      <main className="bg-warm-offWhite text-warm-charcoal">
        <Header />
        <FAQsPage />
        <Footer />
      </main>
    );
  }

  // Render Privacy Policy page
  if (currentPage === 'privacy-policy') {
    return (
      <main className="bg-warm-offWhite text-warm-charcoal">
        <Header />
        <PrivacyPolicyPage />
        <Footer />
      </main>
    );
  }

  // Render Terms and Conditions page
  if (currentPage === 'terms-and-conditions') {
    return (
      <main className="bg-warm-offWhite text-warm-charcoal">
        <Header />
        <TermsAndConditionsPage />
        <Footer />
      </main>
    );
  }

  // Render ALI EULA page
  if (currentPage === 'ali-eula') {
    return (
      <main className="bg-warm-offWhite text-warm-charcoal">
        <Header />
        <ALIEULAPage />
        <Footer />
      </main>
    );
  }

  // Render Engagement Inquiry page
  if (currentPage === 'engagement-inquiry') {
    return (
      <main className="bg-warm-offWhite text-warm-charcoal">
        <Header />
        <EngagementInquiryPage />
        <Footer />
      </main>
    );
  }

  // Render Contact page
  if (currentPage === 'contact') {
    return (
      <main className="bg-warm-offWhite text-warm-charcoal">
        <Header />
        <ContactPage />
        <Footer />
      </main>
    );
  }

  // Render Culture Science pages
  if (currentPage === 'culture-science') {
    return (
      <main className="bg-warm-offWhite text-warm-charcoal">
        <Header />
        <CultureSciencePage />
        <Footer />
      </main>
    );
  }
  if (currentPage === 'scoreboard-leadership') {
    return (
      <main className="bg-warm-offWhite text-warm-charcoal">
        <Header />
        <ScoreboardLeadership />
        <Footer />
      </main>
    );
  }
  if (currentPage === 'bad-leader-project') {
    return (
      <main className="bg-warm-offWhite text-warm-charcoal">
        <Header />
        <BadLeaderProject />
        <Footer />
      </main>
    );
  }
  if (currentPage === 'bad-leader-admin-login') {
    return (
      <main className="bg-warm-offWhite text-warm-charcoal">
        <BadLeaderAdminLogin />
      </main>
    );
  }
  if (currentPage === 'bad-leader-admin') {
    return (
      <main className="bg-warm-offWhite text-warm-charcoal">
        <BadLeaderAdmin />
      </main>
    );
  }
  if (currentPage === 'industry-reports') {
    return (
      <main className="bg-warm-offWhite text-warm-charcoal">
        <Header />
        <IndustryReports />
        <Footer />
      </main>
    );
  }
  if (currentPage === 'ethics') {
    return (
      <main className="bg-warm-offWhite text-warm-charcoal">
        <Header />
        <Ethics />
        <Footer />
      </main>
    );
  }
  // Render Archy pages
  if (currentPage === 'archy') {
    return (
      <main className="bg-warm-offWhite text-warm-charcoal">
        <Header />
        <ArchyPage />
        <Footer />
      </main>
    );
  }

  // Render Home page
  return (
    <>
      <SEO pageKey="home" />
      <main className="bg-white text-[#1a1a1a]">
        <Header />
        <HomePage />
        <Footer />
      </main>
    </>
  );
}